"use client";

import { type FC, useEffect } from "react";
import {
  ExpandableCard,
  type ExpandableCardProps,
  Flex,
  useActions,
  usePubSub,
  type UIAction,
  type UINavigateAction,
  useUuid,
} from "@huub-web/ui";

import { TemplatesParser } from "../../components";
import { useAsyncTemplates } from "../../hooks/useAsyncTemplates";

export type AssessmentTestCardTemplateProps = Pick<
  ExpandableCardProps,
  "disabled" | "open" | "buttonValue"
> & {
  /**
   * Unique id for the card instance
   */
  id?: string;
  /**
   * Card title shown in the closed state
   */
  title: string;
  /**
   * Card description shown below the title in the closed state
   */
  subtitle: string;
  /**
   * Whether the card has an active animated border
   */
  active?: boolean;
  /**
   * Whether the assessment test has been completed
   */
  completed?: boolean;
  /**
   * API endpoint that returns the assessment test template when expanded
   */
  endpoint?: string;
  /**
   * Action to execute when the CTA button is clicked (e.g. navigate)
   */
  action?: UIAction;
  /**
   * Leading icon name for the CTA button
   */
  buttonIcon?: string;
};

export const AssessmentTestCardTemplate: FC<
  AssessmentTestCardTemplateProps
> = ({
  id: idProp,
  title,
  subtitle,
  buttonValue,
  open,
  active,
  completed,
  disabled,
  endpoint,
  action,
  buttonIcon,
}) => {
  const id = useUuid(idProp);
  const pubSub = usePubSub();
  const { handleAction } = useActions();

  const { templatesData, loading, hasLoadedOnce, error, clear } =
    useAsyncTemplates(endpoint, id, open);

  const resolvedContext =
    action?.type === "navigate"
      ? (action as UINavigateAction).payload?.context
      : undefined;

  useEffect(() => {
    if (!open) {
      clear();
    }
  }, [open]);

  const canOpen = !!endpoint || !!action || !!resolvedContext;

  const handleOpen = () => {
    if (action) {
      handleAction(action);
    } else if (resolvedContext) {
      handleAction({
        type: "navigate",
        payload: { context: resolvedContext },
      });
    }
    setTimeout(() => {
      pubSub.publish("scrollToTop", { tag: "carousel" });
    }, 350);
  };

  const handleClose = () => {
    if (resolvedContext) {
      handleAction({
        type: "navigate",
        payload: { context: resolvedContext.slice(0, -1) },
      });
    } else {
      pubSub.publish("refreshTemplates", {});
    }
  };

  return (
    <ExpandableCard
      id={id}
      title={title}
      subtitle={subtitle}
      buttonValue={buttonValue}
      buttonVariant="primary"
      buttonIcon={buttonIcon as any}
      variant={open ? undefined : "infoSurface"}
      border={open ? "gradient" : "transparent"}
      active={active}
      open={!!endpoint && open}
      completed={completed}
      disabled={disabled}
      expandable={!!endpoint}
      loading={loading}
      hasContent={hasLoadedOnce}
      error={error}
      onOpen={canOpen ? handleOpen : undefined}
      onClose={handleClose}
      content={
        templatesData ? (
          <Flex gap="s" direction="column">
            <TemplatesParser level={2} templates={templatesData} />
          </Flex>
        ) : undefined
      }
      menuIcon="documentText"
      menuForcePosition="leadingInside"
    />
  );
};
